import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PaymentForm from './PaymentForm';
import { API_BASE_URL } from '../../config/api.config';
import './Checkout.css';

interface BookingDetails {
  id: string;
  eventDate: string;
  participants: number;
  totalAmount: number;
  depositAmount: number;
  status: string;
  customerName: string;
  customerEmail?: string;
  tripDetails?: {
    variant: string;
    startTime: string;
    duration: string;
    includedMeals: string[];
  };
}

const Checkout: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      
      // Use mock backend API
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBooking({
          id: data.id,
          eventDate: data.eventDate,
          participants: data.participants,
          totalAmount: data.totalAmount,
          depositAmount: data.depositAmount,
          status: data.status,
          customerName: data.customerName,
          customerEmail: data.customerEmail,
          tripDetails: data.tripDetails
        });
      } else {
        throw new Error('Failed to fetch booking details');
      }
    } catch (err) {
      setError('Fehler beim Laden der Buchungsdetails');
      console.error('Error fetching booking:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    navigate('/payment/success', { 
      state: { 
        bookingId: booking?.id,
        amount: booking?.depositAmount 
      }
    });
  };

  if (loading) {
    return (
      <div className="checkout-loading">
        <div className="spinner"></div>
        <p>Lade Buchungsdetails...</p>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="checkout-error">
        <h2>Fehler</h2>
        <p>{error || 'Buchung nicht gefunden'}</p>
        <button onClick={() => navigate('/')}>Zurück zur Startseite</button>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <h1>Zahlung abschließen</h1>
        <p className="booking-number">Buchung #{booking.id}</p>
      </div>

      <div className="checkout-content">
        <div className="booking-summary">
          <h2>Buchungsübersicht</h2>
          
          <div className="summary-card">
            <div className="summary-details">
              <div className="detail-row">
                <span className="label">Kunde:</span>
                <span className="value">{booking.customerName}</span>
              </div>
              <div className="detail-row">
                <span className="label">Veranstaltungsdatum:</span>
                <span className="value">
                  {new Date(booking.eventDate).toLocaleDateString('de-DE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Teilnehmer:</span>
                <span className="value">{booking.participants} Personen</span>
              </div>
              
              {booking.tripDetails && (
                <>
                  <div className="detail-row">
                    <span className="label">Variante:</span>
                    <span className="value">{booking.tripDetails.variant}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Startzeit:</span>
                    <span className="value">{booking.tripDetails.startTime}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Dauer:</span>
                    <span className="value">{booking.tripDetails.duration}</span>
                  </div>
                </>
              )}
            </div>

            <div className="price-summary">
              <div className="price-row">
                <span>Gesamtbetrag:</span>
                <span className="total-amount">€{(booking.totalAmount / 100).toFixed(2)}</span>
              </div>
              <div className="price-row highlight">
                <span>Anzahlung (20%):</span>
                <span className="deposit-amount">€{(booking.depositAmount / 100).toFixed(2)}</span>
              </div>
              <div className="payment-note">
                💡 Die Restzahlung von €{((booking.totalAmount - booking.depositAmount) / 100).toFixed(2)} ist 
                25 Tage vor der Floßfahrt fällig.
              </div>
            </div>
          </div>
        </div>

        <div className="payment-section">
          <PaymentForm 
            bookingId={booking.id}
            amount={booking.depositAmount}
            onSuccess={handlePaymentSuccess}
            onCancel={() => navigate('/dashboard')}
          />
        </div>
      </div>
    </div>
  );
};

export default Checkout;
